import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import MoreHorizIcon from '@mui/icons-material/MoreHoriz';

import Chip from '@shared/atoms/Chip';
import CircularSpinner from '@shared/atoms/CircularSpinner';
import Checkbox from '@shared/atoms/Checkbox';
import IconButton from '@shared/atoms/IconButton';
import HorizontalRule from '@shared/atoms/HorizontalRule';
import SearchField from '@shared/atoms/SearchField';
import Snackbar from '@shared/atoms/Snackbar';
import T from '@shared/atoms/Typography';
import Menu from '@shared/molecules/Menu';
import Table, { CELL_TYPES, ORDER_TYPES } from '@shared/organisms/Table';
import theme from '@shared/themes/default';

import {
  downloadFile,
  formatDate,
  logger,
  newSelectTranslation,
} from '@shared/utils';
import { ORGANIZATION_TYPE, stripeSubscriptionStatus } from '@shared/const';
import { useAppGlobals } from '../../contexts/AppContext';

import CreateOrganizationDialog from './components/CreateOrganizationDialog';
import ExportAllUsersDialog from './components/ExportAllUsersDialog';
import OrganizationsTableRowItem from './components/OrganizationsTableRowItem';
import { listOrganizations } from './graphql/queries';

const ORGANIZATIONS_API = '/organizations/download';

const organizationsLimit = 1000;

const OuterWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.m1};
`;

const SearchWrapper = styled.div`
  display: flex;
  gap: ${theme.spacing.m1};
`;

const CheckboxWrapper = styled.div`
  flex: 1 0 auto;
  height: 40px;
`;

const InfoWrapper = styled.div`
  align-items: center;
  display: flex;
  gap: ${theme.spacing.s};
`;

const OrganizationsView = () => {
  const { useNewGraphQLQuery, getRestClient } = useAppGlobals();
  const { t } = useTranslation();
  const restClient = getRestClient();

  const [anchorEl, setAnchorEl] = useState(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [detailedErrors, setDetailedErrors] = useState([]);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [hasSuccess, setHasSuccess] = useState(false);
  const [listArchived, setListArchived] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [organizations, setOrganizations] = useState([]);
  const [searchValue, setSearchValue] = useState('');
  const [searchedOrganizations, setSearchedOrganizations] = useState([]);
  const [selectedItemIds, setSelectedItemIds] = useState([]);
  const [successMessage, setSuccessMessage] = useState('');

  const customMenuHandler = (event) => {
    setAnchorEl(anchorEl === null ? event.currentTarget : null);
  };

  const {
    data: organizationsData,
    error: organizationsError,
    loading: organizationsLoading,
    fetchMore,
  } = useNewGraphQLQuery(listOrganizations, {
    variables: {
      pagination: { limit: organizationsLimit },
    },
  });

  useEffect(() => {
    const filteredOrganizations = organizations.filter((organization) => {
      const { archived, name } = organization;
      return (
        (listArchived || !archived) &&
        (searchValue.length === 0 ||
          (name || '').toLowerCase().includes(searchValue.toLowerCase()))
      );
    });
    setSearchedOrganizations(filteredOrganizations);
  }, [listArchived, organizations, searchValue]);

  useEffect(() => {
    if (
      !organizationsLoading &&
      organizationsData.listOrganizations.length > 0 &&
      organizationsData.listOrganizations.length % organizationsLimit === 0
    ) {
      fetchMore({
        variables: {
          pagination: {
            limit: organizationsLimit,
            offset: organizationsData.listOrganizations.length,
          },
        },
      });
    }
    if (!organizationsLoading && (organizationsData || organizationsError)) {
      if (organizationsError) {
        setErrorMessage(
          t('backoffice.organizations.failedToLoadOrganizations')
        );
        setHasError(true);
      } else if (organizationsData) {
        setOrganizations(organizationsData.listOrganizations);
      }
    }
  }, [organizationsData, organizationsError, organizationsLoading]);

  useEffect(() => {
    setSelectedItemIds([]);
  }, [listArchived, searchValue]);

  const onCreateOrganization = (organization) => {
    setOrganizations([...organizations, organization]);
  };

  const onUpdateOrganization = (cache, updateOrganization) => {
    cache.modify({
      fields: {
        listOrganizations(existingOrganizations = [], { readField } = {}) {
          return existingOrganizations.map((organizationRef) =>
            readField('id', organizationRef) === updateOrganization.id
              ? { ...organizationRef, ...updateOrganization }
              : organizationRef
          );
        },
      },
    });
  };

  const onExportInvoiceOrganizations = async () => {
    setExportLoading(true);
    try {
      const { data } = await restClient.get(ORGANIZATIONS_API);
      downloadFile(data.signedUrl, data.fileName);
      setExportLoading(false);
      setSuccessMessage(
        t('backoffice.organizations.exportInvoiceOrganizations.success')
      );
      setHasSuccess(true);
    } catch (error) {
      logger.error(error);
      setErrorMessage(
        t('backoffice.organizations.exportInvoiceOrganizations.error')
      );
      setHasError(true);
    }
  };

  const toggleShowExpandedOrganization = (id) => {
    if (selectedItemIds.includes(id)) {
      setSelectedItemIds((items) => items.filter((itemId) => id !== itemId));
    } else {
      setSelectedItemIds((items) => [...items, id]);
    }
  };

  const getSubscriptionChipText = (subscription, type) => {
    const plan =
      type === ORGANIZATION_TYPE.ENTERPRISE
        ? ORGANIZATION_TYPE.ENTERPRISE
        : newSelectTranslation(subscription.product.name);

    if (subscription.invoice) {
      if (subscription.status === stripeSubscriptionStatus.NOT_STARTED) {
        return t('backoffice.organizations.table.planInvoiceNotStarted', {
          plan,
          date: formatDate({ date: subscription.validFrom }),
        });
      }
      return t('backoffice.organizations.table.planInvoice', {
        plan,
      });
    }

    return plan;
  };

  const loading = organizationsLoading || exportLoading;

  return (
    <>
      <OuterWrapper>
        <T fontWeight="medium" variant="headingS">
          {t('backoffice.organizations.organizations')}
        </T>
        <HorizontalRule />
        <SearchWrapper>
          <SearchField
            ariaLabel={t('backoffice.organizations.search')}
            disabled={loading}
            onChange={({ value }) => setSearchValue(value)}
            onClear={() => setSearchValue('')}
            placeholder={t('backoffice.organizations.search')}
            theme="dark"
            type="primary"
            value={searchValue}
          />
          <CheckboxWrapper>
            <Checkbox
              checked={listArchived}
              disabled={loading}
              label={t('backoffice.organizations.listArchived')}
              onChange={() => setListArchived(!listArchived)}
              theme="dark"
            />
          </CheckboxWrapper>
          <IconButton
            ariaLabel={t('generic.more')}
            icon={MoreHorizIcon}
            onClick={customMenuHandler}
            theme="dark"
            type="primary"
          />
          <Menu
            freeMenu
            initAnchorEl={anchorEl}
            menuItems={[
              {
                label: t('backoffice.organizations.createOrganization.title'),
                handler: () => setCreateDialogOpen(true),
              },
              {
                label: t('backoffice.organizations.exportAllUsers.title'),
                handler: () => setExportDialogOpen(true),
              },
              {
                label: t(
                  'backoffice.organizations.exportInvoiceOrganizations.title'
                ),
                handler: onExportInvoiceOrganizations,
              },
            ]}
            setInitAnchorEl={setAnchorEl}
            theme="dark"
          />
        </SearchWrapper>
        {organizationsLoading && <CircularSpinner />}
        {searchedOrganizations.length > 0 && !organizationsLoading && (
          <Table
            ariaLabel="Organizations table"
            columns={[
              {
                key: 'name',
                label: t('backoffice.organizations.table.name'),
                type: CELL_TYPES.COMPONENT,
                render: (row) => {
                  return (
                    <InfoWrapper>
                      {row.name}
                      {row.archived && (
                        <Chip
                          color={theme.palette.errorLight}
                          label={t('backoffice.organizations.table.archived')}
                          small
                          theme="dark"
                        />
                      )}
                    </InfoWrapper>
                  );
                },
              },
              {
                disableSorting: true,
                key: 'subscription',
                label: t('backoffice.organizations.table.info'),
                type: CELL_TYPES.COMPONENT,
                render: (row) => {
                  return (
                    <InfoWrapper>
                      {row.subscription ? (
                        <Chip
                          color={theme.palette.brand}
                          key={row.id}
                          label={getSubscriptionChipText(
                            row.subscription,
                            row.type
                          )}
                          small
                          theme="dark"
                        />
                      ) : (
                        t('backoffice.organizations.table.stopped')
                      )}
                    </InfoWrapper>
                  );
                },
              },
              {
                key: 'createdFrom',
                label: t('backoffice.organizations.table.createdBy'),
              },
              {
                key: 'membersCount',
                label: t('backoffice.organizations.table.registeredMembers'),
              },
              {
                key: 'createdAt',
                label: t('backoffice.organizations.table.createdAt'),
                type: CELL_TYPES.DATE,
              },
            ]}
            items={searchedOrganizations}
            keyField="id"
            defaultOrder={ORDER_TYPES.ASC}
            defaultOrderBy="createdAt"
            rowIsClickable={() => true}
            selectedItemIds={selectedItemIds}
            onRowClick={(row) => toggleShowExpandedOrganization(row.id)}
            ExpandedItemComponent={OrganizationsTableRowItem}
            expandedItemComponentProps={{
              onUpdateOrganization,
              setErrorMessage,
              setDetailedErrors,
              setHasError,
              setHasSuccess,
              setSuccessMessage,
            }}
          />
        )}
        {searchedOrganizations.length === 0 && !organizationsLoading && (
          <T fontWeight="medium" variant="subtitle">
            {t('backoffice.organizations.noOrganizationsFound')}
          </T>
        )}
      </OuterWrapper>
      <CreateOrganizationDialog
        onClose={() => setCreateDialogOpen(false)}
        onCreateOrganization={onCreateOrganization}
        open={createDialogOpen}
        setErrorMessage={setErrorMessage}
        setHasError={setHasError}
        setHasSuccess={setHasSuccess}
        setSuccessMessage={setSuccessMessage}
      />
      <ExportAllUsersDialog
        onClose={() => setExportDialogOpen(false)}
        open={exportDialogOpen}
        setErrorMessage={setErrorMessage}
        setHasError={setHasError}
        setHasSuccess={setHasSuccess}
        setSuccessMessage={setSuccessMessage}
      />
      <Snackbar
        type="success"
        message={successMessage}
        open={hasSuccess}
        onClose={() => setHasSuccess(false)}
      />
      <Snackbar
        type="error"
        message={errorMessage}
        detailedErrors={detailedErrors}
        open={hasError}
        onClose={() => setHasError(false)}
      />
    </>
  );
};

export default OrganizationsView;
